// src/Components/Hooks/useJobProgress.ts
// Polls QR generation job progress while a job is active, then stops on terminal status.

import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { updateJobProgress } from 'slices/sinhBoSoQr/reducer';
import { fetchJobProgress } from 'slices/sinhBoSoQr/thunk';
import { JobProgressMessage, toJobStatus } from 'pages/SinhBoSoQr/types';

const POLLING_INTERVAL_MS = 5000;

const normalizeProgressMessage = (message: any): JobProgressMessage => ({
  jobId: message.jobId ?? message.JobId,
  status: toJobStatus(message.status ?? message.Status),
  processedTickets: message.processedTickets ?? message.ProcessedTickets ?? 0,
  totalTickets: message.totalTickets ?? message.TotalTickets ?? 0,
  progressPercent: message.progressPercent ?? message.ProgressPercent ?? 0,
  errorMessage: message.errorMessage ?? message.ErrorMessage ?? null,
});

export const useJobProgress = (
  jobId: string | null,
  lotteryPeriodId: number | null,
  onProgress?: (message: JobProgressMessage) => void,
) => {
  const dispatch = useDispatch();
  const pollingTimerRef = useRef<number | null>(null);
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;

  const clearPolling = useCallback(() => {
    if (pollingTimerRef.current !== null) {
      window.clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!jobId) return;

    let disposed = false;
    let inFlight = false;

    const handleTerminalStatus = (message: JobProgressMessage) => {
      const status = String(message.status || '').toLowerCase();
      if (status !== 'completed' && status !== 'failed' && status !== 'stalled' && status !== 'cancelled') return;

      clearPolling();
      if (lotteryPeriodId) {
        localStorage.removeItem(`lms_active_job_${lotteryPeriodId}`);
        if (status === 'completed') {
          localStorage.setItem('lms_pending_verify_qr_job', JSON.stringify({
            jobId: message.jobId,
            lotteryPeriodId,
            completedAt: new Date().toISOString(),
          }));
        }
      }
    };

    const pollProgress = async () => {
      if (disposed || inFlight) return;
      inFlight = true;
      try {
        const action = await (dispatch(fetchJobProgress(jobId) as any) as any);
        if (disposed || !action?.payload) return;

        const message = normalizeProgressMessage(action.payload);
        if (message.jobId !== jobId) return;

        onProgressRef.current?.(message);
        dispatch(updateJobProgress({
          jobId: message.jobId,
          status: message.status,
          processedTickets: message.processedTickets,
          totalTickets: message.totalTickets,
          progressPercent: message.progressPercent,
          errorMessage: message.errorMessage || null,
        }));
        handleTerminalStatus(message);
      } catch {
        // Keep polling; transient API failures should not kill progress tracking.
      } finally {
        inFlight = false;
      }
    };

    pollProgress();
    pollingTimerRef.current = window.setInterval(pollProgress, POLLING_INTERVAL_MS);

    return () => {
      disposed = true;
      clearPolling();
    };
  }, [clearPolling, dispatch, jobId, lotteryPeriodId]);

  return {
    disconnect: clearPolling,
  };
};
